import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type RefObject,
} from 'react';
import { GitBranch, X } from 'lucide-react';
import { Button, Dialog, EmptyState, IconButton, Notice } from '@/components';
import { cardDisplayTitle } from '@/lib/card-title';
import type {
  DestinationBoardOption,
  DestinationListOption,
} from '@/lib/cross-board-move';
import { cardMoveNeighbours, type CardMovePlacement } from '@/lib/cross-board-move';
import { useInterfaceLanguage } from '@/lib/interface-language';
import type { LocaleFormattingTranslate } from '@/lib/locale-formatting';

export interface MirrorCardDialogSubmit {
  readonly boardId: string;
  readonly boardTitle: string;
  readonly listId: string;
  readonly listTitle: string;
  readonly previousSurfaceId: string | null;
  readonly nextSurfaceId: string | null;
}

export interface MirrorCardDialogProps {
  readonly open: boolean;
  /**
   * Focused on close instead of the element that opened the dialog, for an
   * opener that no longer exists by then: the card back opens this from an
   * item of its "⋯" Actions menu, and hands focus back to the menu trigger.
   */
  readonly returnFocus?: RefObject<HTMLElement | null> | undefined;
  readonly subjectTitle: string;
  readonly sourceBoardId: string;
  readonly sourceWorkspaceId: string | undefined;
  readonly boards: readonly DestinationBoardOption[];
  readonly listsByBoardId: ReadonlyMap<string, readonly DestinationListOption[]>;
  readonly cardsByListId: ReadonlyMap<string, readonly { id: string; title: string }[]>;
  readonly loading?: boolean | undefined;
  readonly busy?: boolean | undefined;
  readonly onCancel: () => void;
  readonly onMirror: (input: MirrorCardDialogSubmit) => void;
}

export function MirrorCardDialog({
  open,
  subjectTitle,
  sourceBoardId,
  sourceWorkspaceId,
  boards,
  listsByBoardId,
  cardsByListId,
  loading,
  busy,
  onCancel,
  onMirror,
  returnFocus,
}: MirrorCardDialogProps): ReactElement | null {
  const { t } = useInterfaceLanguage();
  const boardSelectId = useId();
  const listSelectId = useId();
  const boardSelect = useRef<HTMLSelectElement | null>(null);
  const cleanTitle = cardDisplayTitle(t, subjectTitle);
  const [selectedBoardId, setSelectedBoardId] = useState('');
  const [selectedListId, setSelectedListId] = useState('');
  const [placement, setPlacement] = useState<CardMovePlacement>('bottom');

  const candidateBoards = useMemo(() => {
    if (sourceWorkspaceId === undefined) return [];
    return boards.filter(
      (board) => board.workspaceId === sourceWorkspaceId && board.id !== sourceBoardId,
    );
  }, [boards, sourceBoardId, sourceWorkspaceId]);

  const selectedBoard =
    candidateBoards.find((board) => board.id === selectedBoardId) ??
    candidateBoards[0] ??
    null;
  const availableLists =
    selectedBoard === null ? [] : (listsByBoardId.get(selectedBoard.id) ?? []);
  const effectiveListId = availableLists.some((list) => list.id === selectedListId)
    ? selectedListId
    : (availableLists[0]?.id ?? '');
  const selectedList = availableLists.find((list) => list.id === effectiveListId) ?? null;
  const positionCards = cardsByListId.get(effectiveListId) ?? [];
  const placementChoices = useMemo(
    () => mirrorPlacementChoices(t, positionCards),
    [positionCards, t],
  );

  useEffect(() => {
    if (!open) return;
    setSelectedBoardId(candidateBoards[0]?.id ?? '');
    setSelectedListId('');
    setPlacement('bottom');
  }, [candidateBoards, open]);

  useEffect(() => {
    if (!open) return;
    setPlacement('bottom');
  }, [effectiveListId, open]);

  if (!open) return null;

  const hasNoDestinationList = selectedBoard !== null && availableLists.length === 0;
  const confirmDisabled =
    busy === true ||
    loading === true ||
    selectedBoard === null ||
    selectedList === null ||
    hasNoDestinationList;
  const disabledReason =
    loading === true
      ? t('boardDashboard.mirror.loadingReason')
      : selectedBoard === null
        ? t('boardDashboard.mirror.chooseBoardReason')
        : hasNoDestinationList
          ? t('boardDashboard.mirror.needsListReason')
          : undefined;
  const busyProps = busy === undefined ? {} : { busy };
  const disabledReasonProps = disabledReason === undefined ? {} : { disabledReason };

  function submitMirror() {
    if (selectedBoard === null || selectedList === null) return;
    const neighbours = cardMoveNeighbours(positionCards, placement);
    onMirror({
      boardId: selectedBoard.id,
      boardTitle: selectedBoard.title,
      listId: selectedList.id,
      listTitle: selectedList.title,
      previousSurfaceId: neighbours.previousCardId,
      nextSurfaceId: neighbours.nextCardId,
    });
  }

  return (
    <Dialog
      {...(returnFocus ? { returnFocus } : {})}
      open
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onCancel();
      }}
      size="md"
      title={t('boardDashboard.mirror.title', { card: cleanTitle })}
      description={t('boardDashboard.mirror.description')}
      initialFocus={boardSelect}
      closeControl={
        <IconButton label={t('boardDashboard.mirror.close')} size="sm" variant="quiet">
          <X aria-hidden="true" className="size-4" />
        </IconButton>
      }
      footer={
        <>
          <Button size="sm" variant="secondary" onClick={onCancel}>
            {t('boardDashboard.mirror.cancel')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            iconStart={<GitBranch aria-hidden="true" className="size-4" />}
            busyLabel={t('boardDashboard.mirror.busy')}
            disabled={confirmDisabled}
            onClick={submitMirror}
            {...busyProps}
            {...disabledReasonProps}
          >
            {t('boardDashboard.mirror.confirm')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Notice
          tone="info"
          title={t('boardDashboard.mirror.noticeTitle')}
          body={t('boardDashboard.mirror.noticeBody')}
        />

        {candidateBoards.length === 0 ? (
          <EmptyState
            tone="neutral"
            headingLevel={3}
            title={t('boardDashboard.mirror.noDestinationTitle')}
            body={t('boardDashboard.mirror.noDestinationBody')}
          />
        ) : (
          <>
            <div className="flex flex-col gap-1">
              <label htmlFor={boardSelectId} className="text-sm font-medium text-ink">
                {t('boardDashboard.mirror.boardLabel')}
              </label>
              <select
                ref={boardSelect}
                id={boardSelectId}
                value={selectedBoard?.id ?? ''}
                onChange={(event) => {
                  setSelectedBoardId(event.currentTarget.value);
                  setSelectedListId('');
                  setPlacement('bottom');
                }}
                className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent"
              >
                {candidateBoards.map((board) => (
                  <option key={board.id} value={board.id}>
                    {t('boardDashboard.mirror.boardOption', {
                      board: board.title,
                      workspace: board.workspaceName,
                    })}
                  </option>
                ))}
              </select>
            </div>

            {hasNoDestinationList ? (
              <EmptyState
                tone="neutral"
                headingLevel={3}
                title={t('boardDashboard.mirror.noListsTitle')}
                body={t('boardDashboard.mirror.noListsBody', {
                  board: selectedBoard?.title ?? t('boardDashboard.mirror.thatBoard'),
                })}
              />
            ) : (
              <div className="flex flex-col gap-1">
                <label htmlFor={listSelectId} className="text-sm font-medium text-ink">
                  {t('boardDashboard.mirror.listLabel')}
                </label>
                <select
                  id={listSelectId}
                  value={selectedList?.id ?? ''}
                  onChange={(event) => {
                    setSelectedListId(event.currentTarget.value);
                    setPlacement('bottom');
                  }}
                  className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent"
                >
                  {availableLists.map((list) => (
                    <option key={list.id} value={list.id}>
                      {list.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {!hasNoDestinationList ? (
              <fieldset className="flex flex-col gap-2">
                <legend className="text-sm font-medium text-ink">
                  {t('boardDashboard.mirror.positionLegend')}
                </legend>
                <div className="flex flex-col gap-1">
                  {placementChoices.map((choice) => (
                    <label
                      key={choice.value}
                      className="flex items-center gap-2 rounded-card px-2 py-1 text-sm text-ink hover:bg-surface-raised"
                    >
                      <input
                        type="radio"
                        name="card-mirror-placement"
                        value={choice.value}
                        checked={placement === choice.value}
                        onChange={() => setPlacement(choice.value)}
                      />
                      <span>{choice.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

/**
 * ⚠ `t` first and required — a module helper cannot call the hook, and a key
 * reached through a variable is invisible to the routing pin's literal-key
 * anchor, so every key below is spelled out at its call site.
 *
 * ---------------------------------------------------------------------------
 * `FE-64.3` — THESE LABELS ARE A SECOND READER OF THE LINK-TARGET REDACTION
 * ---------------------------------------------------------------------------
 * `cards` here is not a list this component owns. `useMoveDestinations`
 * (`../../lib/cross-board-move.ts`) builds `cardsByListId` by mapping
 * `GET /boards/:id/contents` → `list.cards.map(({ id, title }) => …)`, so the
 * destination-board position picker enumerates **every surface in the
 * destination list by title, including link cards** — and it does so for a
 * viewer who is choosing where to put a mirror, not for one who opened that
 * board.
 *
 * That matters because of what `apps/server/src/routes/board-contents.ts`
 * found while closing `FE-64.3`: an internal link card's `cards.title` column
 * **is the pasted URL**, and `cardPath` puts `slugify(<target title>)` in that
 * URL, so before the redaction landed this radio list published the title of a
 * card on a third board to anybody who opened this dialog. The projection now
 * neutralises `title`, `path` and `linkTargetUrl` together in the unavailable
 * case, and this component inherits that for free precisely **because it
 * renders the projected title verbatim as product data** rather than
 * reconstructing anything from `linkTargetUrl`. `MirrorCardDialog.test.tsx`
 * pins the absence over the whole subtree, lower-cased on both sides — a
 * case-sensitive negative cannot see a slug, which is the lesson §9.4 of the
 * audit paid for.
 *
 * ⚠ `cardDisplayTitle` and not `card.title` — the one thing this file did get
 * wrong. Every other card-title author in the product (`CardTile`, `QuickEdit`,
 * `BoardMapView`, `BoardFace`) routes through that leaf, and this one did not,
 * so a blank-titled destination card rendered a radio with an **empty
 * accessible name**: unreachable by name, indistinguishable from its
 * neighbours, and silent to a screen reader. It is a fallback, so it never
 * touches the redacted title, which is never blank.
 */
function mirrorPlacementChoices(
  t: LocaleFormattingTranslate,
  cards: readonly { id: string; title: string }[],
): readonly { value: CardMovePlacement; label: string }[] {
  return [
    { value: 'top', label: t('boardDashboard.mirror.position.top') },
    { value: 'bottom', label: t('boardDashboard.mirror.position.bottom') },
    ...cards.map((card) => ({
      value: `after:${card.id}` as const,
      label: t('boardDashboard.mirror.position.afterCard', {
        card: cardDisplayTitle(t, card.title),
      }),
    })),
  ];
}
